<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('urlCodec.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('urlCodec.title') }}</h1>
      <p>{{ $t('urlCodec.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="codec-layout">
        <div class="codec-panel">
          <label>{{ $t('urlCodec.inputLabel') }}</label>
          <textarea
            v-model="input"
            :placeholder="$t('urlCodec.inputPlaceholder')"
            rows="8"
            spellcheck="false"
          ></textarea>
        </div>

        <div class="codec-actions">
          <button class="btn btn-primary" @click="encode">{{ $t('urlCodec.encode') }} →</button>
          <button class="btn btn-secondary" @click="decode">← {{ $t('urlCodec.decode') }}</button>
          <button class="btn btn-secondary" @click="encodeComponent">{{ $t('urlCodec.encodeComponent') }} →</button>
        </div>

        <div class="codec-panel">
          <label>{{ $t('urlCodec.outputLabel') }}</label>
          <textarea
            v-model="output"
            :placeholder="$t('urlCodec.outputPlaceholder')"
            rows="8"
            spellcheck="false"
            readonly
          ></textarea>
        </div>
      </div>

      <div class="action-bar">
        <span class="status-msg" v-if="statusMsg">{{ statusMsg }}</span>
        <button class="btn btn-outline" @click="copyResult">{{ $t('common.copy') }}</button>
        <button class="btn btn-outline" @click="swap">{{ $t('base64.swap') }}</button>
        <button class="btn btn-outline" @click="clear">{{ $t('common.clear') }}</button>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('urlCodec.whatIs') }}</h2>
      <p>{{ $t('urlCodec.whatIsDesc') }}</p>
      <h3>{{ $t('urlCodec.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('urlCodec.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h3>{{ $t('urlCodec.tipsTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('urlCodec.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UrlCodecTool',
  data() {
    return { input: '', output: '', statusMsg: '' }
  },
  methods: {
    encode() {
      if (!this.input.trim()) return
      try {
        this.output = encodeURI(this.input)
        this.statusMsg = this.$t('urlCodec.encodeSuccess')
      } catch (e) {
        this.statusMsg = this.$t('urlCodec.error')
      }
    },
    encodeComponent() {
      if (!this.input.trim()) return
      try {
        this.output = encodeURIComponent(this.input)
        this.statusMsg = this.$t('urlCodec.encodeSuccess')
      } catch (e) {
        this.statusMsg = this.$t('urlCodec.error')
      }
    },
    decode() {
      if (!this.input.trim()) return
      try {
        this.output = decodeURIComponent(this.input.trim())
        this.statusMsg = this.$t('urlCodec.decodeSuccess')
      } catch (e) {
        this.statusMsg = this.$t('urlCodec.decodeError')
      }
    },
    swap() {
      const tmp = this.input
      this.input = this.output
      this.output = tmp
    },
    clear() { this.input = ''; this.output = ''; this.statusMsg = '' },
    async copyResult() {
      if (!this.output) return
      try {
        await navigator.clipboard.writeText(this.output)
        this.statusMsg = this.$t('common.copied')
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1000px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }
.codec-layout { display: flex; gap: 16px; align-items: stretch; }
.codec-panel { flex: 1; display: flex; flex-direction: column; }
.codec-panel label { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.codec-panel textarea { flex: 1; min-height: 180px; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 14px; resize: vertical; outline: none; transition: border-color 0.2s; }
.codec-panel textarea:focus { border-color: var(--accent-color); }
.codec-actions { display: flex; flex-direction: column; justify-content: center; gap: 10px; padding: 0 4px; }
.btn { padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; white-space: nowrap; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-secondary:hover { border-color: var(--accent-color); }
.btn-outline { background: transparent; color: var(--text-primary); border: 1px solid var(--border-color); padding: 8px 16px; }
.btn-outline:hover { border-color: var(--accent-color); color: var(--accent-color); }
.action-bar { display: flex; align-items: center; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); flex: 1; }
@media (max-width: 768px) {
  .codec-layout { flex-direction: column; }
  .codec-actions { flex-direction: row; justify-content: center; flex-wrap: wrap; }
}
</style>
